// CR-2: "Send Rejoin Link" for Inactive rows of the Employees tab, and HR's confirm box.
// Self-contained: the Employees tab only renders <RejoinLinkButton> and <RejoinLinkPopup>.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiSend } from "react-icons/fi";
import Select from "react-select";
import { Modal } from "./ExitParts";
import { exitApi } from "./exitApi";

// Eligible for Rehire (CR-3) and rejoin status for the rows on the current page, keyed by user id.
// `enabled` stays false for users who are not exit HR (the API answers 403), so the tab looks
// exactly as before for them.
export function useRejoinStatus(rows) {
  const [statusMap, setStatusMap] = useState({});
  const [enabled, setEnabled] = useState(false);
  const key = (rows || [])
    .map((r) => r?.user_id?.id)
    .filter(Boolean)
    .join(",");

  const refresh = useCallback(async () => {
    if (!key) {
      setStatusMap({});
      return;
    }
    try {
      const res = await exitApi.rejoinStatus(key.split(","));
      setStatusMap(Object.fromEntries((res.data || []).map((s) => [s.employee_id, s])));
      setEnabled(true);
    } catch {
      setStatusMap({});
      setEnabled(false);
    }
  }, [key]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { statusMap, enabled, refresh };
}

// CR-3 column value: the Yes / No saved on the latest exit (bulk sheet or default, then HR), "--" with no exit.
export function RehireCell({ status }) {
  // Same markup as the Status column next to it.
  const value = status?.eligible_for_rehire;
  const color = value === "Yes" ? "green" : value === "No" ? "red" : value ? "#a15c00" : undefined;
  return <p className="p-0 m-0" style={{ color, whiteSpace: "nowrap" }}>{value || "--"}</p>;
}

// Shown on Inactive rows only. Enabled only when the exit marked the employee eligible for rehire;
// otherwise greyed out with the reason.
// `enabled` is false for users who are not exit HR: nothing is rendered and the row is unchanged.
export function RejoinLinkButton({ enabled, status, inactive, onClick }) {
  if (!enabled) return null;
  // Same markup as the tab's Delete/Edit/Exports buttons (icon in the 20px .EditImages box, label on
  // hover) so rows stay aligned. Not `disabled`, so the "not eligible" tooltip still shows on hover.
  if (!status || !inactive) {
    // Rows without the action keep an invisible button of the same size, so every row has the same
    // number of action buttons and the icons line up down the column.
    return (
      <button type="button" className="btn Status" tabIndex={-1} aria-hidden="true" style={{ visibility: "hidden" }}>
        <span className="OuterActionSpan">
          <span className="EditImages">
            <FiSend style={{ width: "100%", height: "100%" }} />
          </span>
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className="btn Status"
      title={status.can_send ? "Send Rejoin Link" : status.reason}
      aria-disabled={!status.can_send}
      style={status.can_send ? undefined : { opacity: 0.4, cursor: "not-allowed" }}
      onClick={(e) => {
        e.stopPropagation();
        if (status.can_send) onClick();
      }}
    >
      <span className="OuterActionSpan">
        <span className="EditImages">
          <FiSend style={{ width: "100%", height: "100%", color: "#ef4d39" }} />
        </span>
        <span className="ActionsList"> Rejoin </span>
      </span>
    </button>
  );
}

// Over the modal (overflow hidden), so the client list is not cut off.
// const SELECT_STYLES = { menuPortal: (base) => ({ ...base, zIndex: 1100 }) };
// One look for all four dropdowns, in the exit screens' colours (see ExitWorkflow.scss).
const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 1100 }),
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 4,
    backgroundColor: state.isDisabled ? "#eef0f3" : "#fff",
    borderColor: state.selectProps.hasError ? "#c62828" : state.isFocused ? "#f26639" : "#dfe2e7",
    boxShadow: state.isFocused ? "0 0 0 3px #fde1d6" : "none",
    "&:hover": { borderColor: state.isFocused ? "#f26639" : "#c9cdd4" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
  placeholder: (base) => ({ ...base, color: "#9aa0a8" }),
  singleValue: (base) => ({ ...base, color: "#2b2f36" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({ ...base, color: state.isFocused ? "#f26639" : "#9aa0a8", padding: "0 10px" }),
  menu: (base) => ({
    ...base, zIndex: 20, marginTop: 4, marginBottom: 4, borderRadius: 6, overflow: "hidden",
    border: "1px solid #dfe2e7", boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  }),
  menuList: (base) => ({ ...base, maxHeight: 220, padding: 4 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 4,
    fontSize: 14,
    padding: "8px 10px",
    lineHeight: 1.35,
    whiteSpace: "normal",
    wordBreak: "break-word",
    cursor: "pointer",
    color: state.isSelected ? "#fff" : "#2b2f36",
    backgroundColor: state.isSelected ? "#ef4d39" : state.isFocused ? "#fff1ea" : "#fff",
    "&:active": { backgroundColor: state.isSelected ? "#ef4d39" : "#fde1d6" },
  }),
  noOptionsMessage: (base) => ({ ...base, fontSize: 13 }),
};

// Confirm box: mobile and personal email from the record plus the client, designation, employee type and
// category for the Denave self-onboarding invite (pre-filled from the old onboarding), editable before sending.
export function RejoinLinkPopup({ employeeId, onClose, onSent }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    mobile: "", email: "", client_id: "", designation_id: "", employee_type: "", employee_category: "",
  });
  const [options, setOptions] = useState({ clients: [], employee_types: [], employee_categories: [] });
  const [designations, setDesignations] = useState([]);
  // the employee's last designation for the summary card - kept when HR picks another client / designation
  const [lastDesignation, setLastDesignation] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([exitApi.rejoinDetail(employeeId), exitApi.rejoinOptions()])
      .then(([res, opts]) => {
        const d = res.data;
        setDetail(d);
        setOptions(opts.data);
        setForm({
          mobile: d.mobile || "", email: d.email || "", client_id: d.client_id || "",
          designation_id: d.designation_id || "", employee_type: d.employee_type || "", employee_category: d.employee_category || "",
        });
      })
      .catch(() => onClose());
    // Load once per employee; onClose is an inline function from the Employees tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // Designations of the chosen client.
  useEffect(() => {
    if (!form.client_id) {
      setDesignations([]);
      return;
    }
    exitApi.rejoinOptions(form.client_id).then((res) => setDesignations(res.data.designations)).catch(() => setDesignations([]));
  }, [form.client_id]);

  // first time the old client's designations arrive, remember the pre-filled one's name
  useEffect(() => {
    if (lastDesignation || !detail?.designation_id || form.client_id !== detail.client_id) return;
    const old = designations.find((d) => d.id === detail.designation_id);
    if (old) setLastDesignation(old.name);
  }, [designations, detail, form.client_id, lastDesignation]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const setClient = (clientId) => {
    setForm((f) => ({ ...f, client_id: clientId, designation_id: "" }));
    setErrors((e) => ({ ...e, client_id: undefined, designation_id: undefined }));
  };

  const send = async () => {
    const errs = {};
    if (!/^\d{10}$/.test(form.mobile.trim())) errs.mobile = "Enter a valid 10 digit mobile number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email address.";
    if (!form.client_id) errs.client_id = "Select the client.";
    if (!form.designation_id) errs.designation_id = "Select the designation.";
    if (!form.employee_type) errs.employee_type = "Select the employee type.";
    if (!form.employee_category) errs.employee_category = "Select the employee category.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setBusy(true);
    try {
      await exitApi.rejoinSend(employeeId, { ...form, mobile: form.mobile.trim(), email: form.email.trim() });
      toast.success(`Onboarding link sent to ${form.email.trim()}.`);
      onSent && onSent();
      onClose();
      // the old employee record is removed by the send - reload so the Employees list shows it, after the
      // success message has been seen
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      if (e.details && Object.keys(e.details).length) setErrors(e.details);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  // const clientOptions = options.clients.map((c) => ({ value: c.id, label: c.name }));
  // Plain <select> looked greyed out (the app has no .form-select style), so every dropdown is the same
  // react-select as Client now.
  // const selectField = (key, label, items, valueKey, labelKey) => (
  //   <div className={`exitwf-field ${errors[key] ? "has-error" : ""}`}>
  //     <label htmlFor={`rejoin-${key}`}>{label}<span className="req">*</span></label>
  //     <select id={`rejoin-${key}`} className="form-select" value={form[key]} disabled={busy}
  //       onChange={(e) => set(key, e.target.value)}>
  //       <option value="">Select</option>
  //       {items.map((o) => (
  //         <option key={o[valueKey]} value={o[valueKey]}>{o[labelKey]}</option>
  //       ))}
  //     </select>
  //     {errors[key] && <div className="err">{errors[key]}</div>}
  //   </div>
  // );
  const selectField = (key, label, items, valueKey, labelKey, { onChange, searchable = true, placeholder = "Select", empty = "No options" } = {}) => {
    const opts = items.map((o) => ({ value: o[valueKey], label: o[labelKey] }));
    return (
      <div className={`exitwf-field ${errors[key] ? "has-error" : ""}`}>
        <label htmlFor={`rejoin-${key}`}>{label}<span className="req">*</span></label>
        <Select inputId={`rejoin-${key}`} options={opts} isDisabled={busy} isSearchable={searchable}
          placeholder={placeholder} noOptionsMessage={({ inputValue }) => (inputValue ? `No match for "${inputValue}"` : empty)}
          hasError={!!errors[key]}
          value={opts.find((o) => o.value === form[key]) || null}
          onChange={(o) => (onChange ? onChange(o ? o.value : "") : set(key, o ? o.value : ""))}
          // menu drawn inside the popup, right under its box - the body portal placed it off to the side
          // menuPortalTarget={document.body} menuPosition="fixed" menuPlacement="auto" styles={SELECT_STYLES} />
          menuPlacement="auto" styles={SELECT_STYLES} />
        {errors[key] && <div className="err">{errors[key]}</div>}
      </div>
    );
  };
  // const designationName = designations.find((d) => d.id === form.designation_id)?.name;

  return (
    // .exitwf only scopes the exit styles; the modal itself is fixed-position, so no padding here.
    // rejoin-popup: lets the dropdown lists show past the popup's edge (ExitWorkflow.scss).
    <div className="exitwf rejoin-popup" style={{ padding: 0 }}>
      <Modal
        title="Send Rejoin Link"
        onClose={onClose}
        footer={
          <>
            <button className="exitwf-btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
            <button className="exitwf-btn" disabled={busy || !detail?.can_send} onClick={send}>
              {busy ? "Sending..." : "Send"}
            </button>
          </>
        }
      >
        {!detail ? (
          <p>Loading...</p>
        ) : (
          <>
            {/* <p style={{ marginBottom: 14 }}>
              The Denave self-onboarding (KYC) link will be sent to <b>{detail.name}</b> by email. The old employee
              record is removed now and a new one is created when the employee signs the form.
            </p> */}
            <div className="rejoin-summary">
              <div className="rejoin-summary-head">
                <span className="rejoin-name">{detail.name}</span>
                <span className="rejoin-badge inactive">Inactive</span>
                {detail.can_send && <span className="rejoin-badge ok">Eligible for Rehire</span>}
              </div>
              <div className="rejoin-summary-row">
                <span><em>Last client</em>{detail.client_name || "--"}</span>
                <span><em>Last designation</em>{lastDesignation || "--"}</span>
                <span><em>Exit</em>{detail.case_no || "--"}</span>
              </div>
            </div>
            {!detail.can_send && <p className="exitwf-hint">{detail.reason}</p>}
            <div className="rejoin-section">Contact details</div>
            <div className="rejoin-grid">
              <div className={`exitwf-field ${errors.mobile ? "has-error" : ""}`}>
                <label htmlFor="rejoin-mobile">Mobile Number<span className="req">*</span></label>
                {/* <input id="rejoin-mobile" className="form-control" maxLength={10} value={form.mobile} disabled={busy}
                  onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} /> */}
                {/* from the employee record - not editable here */}
                <input id="rejoin-mobile" className="form-control" maxLength={10} value={form.mobile} disabled readOnly />
                {errors.mobile && <div className="err">{errors.mobile}</div>}
              </div>
              <div className={`exitwf-field ${errors.email ? "has-error" : ""}`}>
                <label htmlFor="rejoin-email">Personal Email<span className="req">*</span></label>
                {/* <input id="rejoin-email" className="form-control" value={form.email} disabled={busy}
                  onChange={(e) => set("email", e.target.value)} /> */}
                {/* from the employee record - not editable here */}
                <input id="rejoin-email" className="form-control" value={form.email} disabled readOnly />
                {errors.email && <div className="err">{errors.email}</div>}
              </div>
            </div>
            <div className="rejoin-section">New posting</div>
            <div className="rejoin-grid">
              {/* <div className={`exitwf-field ${errors.client_id ? "has-error" : ""}`}>
                <label htmlFor="rejoin-client_id">Client<span className="req">*</span></label>
                <Select inputId="rejoin-client_id" options={clientOptions} isDisabled={busy} placeholder="Search client"
                  value={clientOptions.find((o) => o.value === form.client_id) || null}
                  onChange={(o) => setClient(o ? o.value : "")}
                  menuPortalTarget={document.body} styles={SELECT_STYLES} />
                {errors.client_id && <div className="err">{errors.client_id}</div>}
              </div> */}
              {selectField("client_id", "Client", options.clients, "id", "name", { onChange: setClient, placeholder: "Search client", empty: "No clients" })}
              {selectField("designation_id", "Designation", designations, "id", "name", {
                placeholder: form.client_id ? "Search designation" : "Select a client first",
                empty: form.client_id ? "No designations in this client" : "Select a client first",
              })}
              {selectField("employee_type", "Employee Type", options.employee_types, "value", "label", { searchable: false })}
              {selectField("employee_category", "Employee Category", options.employee_categories, "value", "label", { searchable: false })}
            </div>
            <div className="rejoin-note">
              The KYC link is emailed to the employee and is valid for 48 hours.
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
